/**
 * React Query hooks for in-app notifications.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '../notificationService';

const QUERY_KEY = ['notifications'];

export function useNotifications() {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => notificationService.getAll(),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

/** UC12/13: Filtered notifications for the current user based on role and record ownership. */
export function useNotificationsForUser(userId: string, userRoles: string[] = [], ownedRecordIds: string[] = []) {
  return useQuery({
    queryKey: [...QUERY_KEY, 'user', userId, userRoles.join(',')],
    queryFn: () => notificationService.getForUser(userId, userRoles, ownedRecordIds),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: [...QUERY_KEY, 'unread-count'],
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: 15_000,
    staleTime: 5_000,
  });
}

export function useUnreadNotificationCountForUser(userId: string, userRoles: string[] = [], ownedRecordIds: string[] = []) {
  return useQuery({
    queryKey: [...QUERY_KEY, 'unread-count', 'user', userId],
    queryFn: () => notificationService.getUnreadCountForUser(userId, userRoles, ownedRecordIds),
    refetchInterval: 15_000,
    staleTime: 5_000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      notificationService.markAsRead(id);
      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      notificationService.markAllAsRead();
      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
